import { Component, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SmoothScroll from "@/components/SmoothScroll";
import { SiteLayout, ScrollToTop } from "@/components/site/Chrome";
import { Logo } from "@/components/site/Logo";
import Home from "@/pages/Home";
import CategoryPage from "@/pages/CategoryPage";
import ProductDetail from "@/pages/ProductDetail";
import RebatesPage from "@/pages/RebatesPage";
import InstallersPage from "@/pages/InstallersPage";
import TradeStcPage from "@/pages/TradeStcPage";
import ContactPage from "@/pages/ContactPage";
import InspectionPage from "@/pages/InspectionPage";
import FaqPage from "@/pages/FaqPage";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminShell from "@/pages/admin/AdminShell";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminRebates from "@/pages/admin/AdminRebates";
import AdminFaqs from "@/pages/admin/AdminFaqs";

class ErrorBoundary extends Component {
  state = { error: null };
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    console.error("Unhandled render error:", error, info);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-bone px-6 text-center">
          <Logo />
          <h1 className="font-display text-2xl font-bold text-forest-700">
            Something's gone wrong on our side.
          </h1>
          <button
            onClick={() => window.location.reload()}
            data-testid="error-reload-button"
            className="rounded-full bg-terra px-6 py-3 text-sm font-bold text-bone transition-colors hover:bg-terra-dark"
          >
            Reload the site
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function RequireAuth({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-forest-800 text-bone">
        <p className="text-sm font-semibold tracking-[0.2em] uppercase" data-testid="admin-auth-loading">
          Checking your session…
        </p>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <SmoothScroll>
        <ScrollToTop />
        <AuthProvider>
          <ErrorBoundary>
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/products/:slug" element={<CategoryPage />} />
                <Route path="/products/:slug/:pid" element={<ProductDetail />} />
                <Route path="/rebates" element={<RebatesPage />} />
                <Route path="/installers" element={<InstallersPage />} />
                <Route path="/trade-stcs" element={<TradeStcPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/book-inspection" element={<InspectionPage />} />
                <Route path="/faq" element={<FaqPage />} />
              </Route>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route
                path="/admin"
                element={
                  <RequireAuth>
                    <AdminShell />
                  </RequireAuth>
                }
              >
                <Route index element={<AdminLeads />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="rebates" element={<AdminRebates />} />
                <Route path="faqs" element={<AdminFaqs />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </ErrorBoundary>
        </AuthProvider>
      </SmoothScroll>
    </BrowserRouter>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-bone px-6 text-center">
      <Logo />
      <h1 className="font-display text-3xl font-extrabold text-forest-700">Page not found</h1>
      <p className="text-moss">That page doesn't exist — let's get you back to safety.</p>
      <a href="/" data-testid="notfound-home-link" className="rounded-full bg-terra px-6 py-3 text-sm font-bold text-bone">
        Back to home
      </a>
    </div>
  );
}

export default App;
